import { useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { Plus, Trash2 } from "lucide-react";
import { AgentTool, KnowledgeBasesV2Service } from "@/generated";
import { Button } from "@/components/shadcn/button";
import { Label } from "@/components/shadcn/label";
import { RadioGroup, RadioGroupItem } from "@/components/shadcn/radio-group";
import { NoxusInput } from "@/components/Form/InputField";
import { SpotSelect } from "@/components/Form/SelectField";
import { MultiSelect } from "@/components/Form/MultiSelectField";
import { NumberInput } from "@/components/Form/NumberInputField";
import { Toggle } from "@/components/Form/ToggleField";
import { NoxusFormLabel } from "@/components/Form/NoxusFormLabel";
import { isRecord } from "@/utils/isRecord";
import { useSpotUser } from "@/utils/spotUser";
import { useDebouncedCallback } from "@/components/Fields/useDebouncedCallback";
import { SelectedFileRow } from "@/components/Form/SelectedFileRow";
import { DATA_KEYS } from "@/utils/useRemoteData";
import {
  COMPOSITE_CHILD_TYPES,
  CheckDraft,
  Expected,
  argumentText,
  checkLabel,
  compiles,
  compositeChildren,
  defaultExpected,
} from "./checkCatalog";

export type KnowledgeBaseOption = { id: string; name: string };

export type CheckContext = {
  tools: AgentTool[];
  knowledgeBases: KnowledgeBaseOption[];
};

type FieldsProps = {
  type: string;
  expected: Expected;
  onChange: (expected: Expected) => void;
  context: CheckContext;
};

function text(expected: Expected, key: string): string {
  const value = expected[key];
  return typeof value === "string" ? value : "";
}

function strings(expected: Expected, key: string): string[] {
  const value = expected[key];
  return Array.isArray(value)
    ? value.filter((v): v is string => typeof v === "string")
    : [];
}

function TextField({
  name,
  label,
  placeholder,
  value,
  onChange,
  isTextarea = false,
  helperText,
}: {
  name: string;
  label: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  isTextarea?: boolean;
  helperText?: string;
}) {
  return (
    <NoxusInput
      id={`check-${name}`}
      name={`check-${name}`}
      label={label}
      placeholder={placeholder}
      value={value}
      helperText={helperText}
      isTextarea={isTextarea}
      isAutoResize={isTextarea}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function ToolSelect({
  value,
  onChange,
  tools,
}: {
  value: string;
  onChange: (name: string) => void;
  tools: AgentTool[];
}) {
  const t = useTranslations("agentTests");
  const options = tools.map((tool) => ({
    value: tool.name,
    label: tool.name,
    description: tool.description ?? undefined,
  }));
  if (value && !tools.some((tool) => tool.name === value)) {
    options.unshift({
      value,
      label: value,
      description: t("checks.fields.toolGone"),
    });
  }
  return (
    <SpotSelect
      name="check-tool"
      label={t("checks.fields.tool")}
      placeholder={t("checks.fields.chooseTool")}
      options={options}
      value={value || undefined}
      helperText={tools.length === 0 ? t("checks.fields.noTools") : undefined}
      onChange={(option: { value: string } | null) =>
        onChange(option?.value ?? "")
      }
    />
  );
}

function parametersOf(tools: AgentTool[], toolName: string) {
  return tools.find((tool) => tool.name === toolName)?.parameters ?? [];
}

function coerce(raw: string, type: string | null | undefined): unknown {
  if (type === "string") {
    return raw;
  }
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

type ArgumentRow = { name: string; value: string };

function ArgumentValues({
  expected,
  onChange,
  tools,
}: {
  expected: Expected;
  onChange: (expected: Expected) => void;
  tools: AgentTool[];
}) {
  const t = useTranslations("agentTests");
  const toolName = text(expected, "tool_name");
  const parameters = parametersOf(tools, toolName);
  const [rows, setRows] = useState<ArgumentRow[]>(() => {
    const args = isRecord(expected.expected_args) ? expected.expected_args : {};
    const stored = Object.entries(args).map(([name, value]) => ({
      name,
      value: argumentText(value),
    }));
    return stored.length > 0 ? stored : [{ name: "", value: "" }];
  });

  const update = (next: ArgumentRow[]) => {
    setRows(next);
    const typeOf = (name: string) =>
      parameters.find((p) => p.name === name)?.type;
    onChange({
      ...expected,
      expected_args: Object.fromEntries(
        next
          .filter((row) => row.name && row.value !== "")
          .map((row) => [row.name, coerce(row.value, typeOf(row.name))])
      ),
    });
  };

  const options = parameters.map((p) => ({
    value: p.name,
    label: p.name,
    description: p.type ?? undefined,
  }));

  return (
    <div className="flex flex-col gap-2">
      <NoxusFormLabel
        label={t("checks.fields.arguments")}
        tooltip={t("checks.fields.argumentsTooltip")}
      />
      {rows.map((row, index) => (
        <div
          key={index}
          data-testid="argument-rule"
          className="flex items-start gap-2"
        >
          <div className="w-2/5">
            <SpotSelect
              name={`argument-${index}`}
              placeholder={t("checks.fields.argument")}
              options={options}
              value={row.name || undefined}
              onChange={(option: { value: string } | null) =>
                update(
                  rows.map((r, i) =>
                    i === index ? { ...r, name: option?.value ?? "" } : r
                  )
                )
              }
            />
          </div>
          <span className="text-sm text-muted-foreground pt-2">
            {t("checks.fields.equals")}
          </span>
          <div className="flex-1">
            <NoxusInput
              id={`argument-value-${index}`}
              name={`argument-value-${index}`}
              placeholder={t("checks.fields.value")}
              value={row.value}
              onChange={(e) =>
                update(
                  rows.map((r, i) =>
                    i === index ? { ...r, value: e.target.value } : r
                  )
                )
              }
            />
          </div>
          <Button
            type="button"
            aria-label={t("checks.fields.removeArgument")}
            size="iconsm"
            variant="ghostDestructive"
            className="mt-1"
            disabled={rows.length === 1}
            onClick={() => update(rows.filter((_, i) => i !== index))}
          >
            <Trash2 />
          </Button>
        </div>
      ))}
      <div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => update([...rows, { name: "", value: "" }])}
        >
          <Plus className="size-4" />
          {t("checks.fields.addArgument")}
        </Button>
      </div>
    </div>
  );
}

function ArgumentKeys({
  expected,
  onChange,
  tools,
}: {
  expected: Expected;
  onChange: (expected: Expected) => void;
  tools: AgentTool[];
}) {
  const t = useTranslations("agentTests");
  const options = parametersOf(tools, text(expected, "tool_name")).map((p) => ({
    value: p.name,
    label: p.name,
  }));
  const selected = (key: string) =>
    strings(expected, key).map((name) => ({ value: name, label: name }));
  return (
    <>
      <MultiSelect
        name="check-present"
        label={t("checks.fields.mustBePresent")}
        placeholder={t("checks.fields.arguments")}
        options={options}
        value={selected("present")}
        onChange={(values) =>
          onChange({ ...expected, present: values.map((v) => v.value) })
        }
      />
      <MultiSelect
        name="check-absent"
        label={t("checks.fields.mustBeAbsent")}
        placeholder={t("checks.fields.arguments")}
        options={options}
        value={selected("absent")}
        onChange={(values) =>
          onChange({ ...expected, absent: values.map((v) => v.value) })
        }
      />
    </>
  );
}

function RefusalChoice({
  expected,
  onChange,
}: {
  expected: Expected;
  onChange: (expected: Expected) => void;
}) {
  const t = useTranslations("agentTests");
  const options = [
    {
      value: "refuses",
      label: t("checks.fields.refusal.refuses"),
      description: t("checks.fields.refusal.refusesDescription"),
    },
    {
      value: "answers",
      label: t("checks.fields.refusal.answers"),
      description: t("checks.fields.refusal.answersDescription"),
    },
  ];
  return (
    <div className="flex flex-col gap-2">
      <Label className="text-sm font-medium">
        {t("checks.fields.refusal.theAgent")}
      </Label>
      <RadioGroup
        value={expected.expect_refusal === false ? "answers" : "refuses"}
        onValueChange={(value) =>
          onChange({ ...expected, expect_refusal: value === "refuses" })
        }
        className="flex flex-col gap-2"
      >
        {options.map((option) => (
          <label
            key={option.value}
            htmlFor={`refusal-${option.value}`}
            className="flex items-start gap-3 rounded-md border p-3 cursor-pointer hover:border-primary/50"
          >
            <RadioGroupItem
              id={`refusal-${option.value}`}
              value={option.value}
              className="mt-0.5"
            />
            <div className="flex flex-col gap-0.5">
              <span className="text-sm font-medium">{option.label}</span>
              <span className="text-xs text-muted-foreground">
                {option.description}
              </span>
            </div>
          </label>
        ))}
      </RadioGroup>
    </div>
  );
}

export type PickedDocument = {
  kb_id: string;
  doc_id: string;
  file_name: string;
};

const DOCUMENT_PAGE_SIZE = 20;

function isPicked(picked: PickedDocument[], docId: string, name: string) {
  return picked.some(
    (d) => d.doc_id === docId || (!d.doc_id && d.file_name === name)
  );
}

function KnowledgeBaseDocumentSearch({
  knowledgeBase,
  picked,
  onPick,
}: {
  knowledgeBase: KnowledgeBaseOption;
  picked: PickedDocument[];
  onPick: (document: PickedDocument) => void;
}) {
  const t = useTranslations("agentTests");
  const { spotGroup } = useSpotUser();
  const [search, setSearch] = useState("");
  const searchLater = useDebouncedCallback(setSearch, 300);
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: [DATA_KEYS.KNOWLEDGE_BASE_DOCUMENTS, knowledgeBase.id, search],
      queryFn: ({ pageParam }) =>
        KnowledgeBasesV2Service.getKnowledgeBaseDocuments({
          path: {
            group_id: spotGroup.id,
            knowledge_base_id: knowledgeBase.id,
            status: "trained",
          },
          query: { query: search, page: pageParam, size: DOCUMENT_PAGE_SIZE },
        }),
      initialPageParam: 1,
      getNextPageParam: (lastPage) => {
        const { page, pages } = lastPage.data;
        return page && pages && page < pages ? page + 1 : undefined;
      },
    });
  const options = (data?.pages ?? [])
    .flatMap((page) => page.data.items)
    .flatMap((d) =>
      d.name && !isPicked(picked, d.id, d.name)
        ? [
            {
              value: d.id,
              label: d.name,
              description: d.prefix && d.prefix !== "/" ? d.prefix : undefined,
            },
          ]
        : []
    );
  return (
    <SpotSelect
      name={`check-documents-${knowledgeBase.id}`}
      label={knowledgeBase.name}
      placeholder={t("checks.fields.searchDocuments")}
      options={options}
      value={undefined}
      isLoading={isLoading}
      onSearchChange={searchLater}
      onLoadMore={fetchNextPage}
      hasMore={hasNextPage}
      isLoadingMore={isFetchingNextPage}
      onChange={(option: { value: string; label: string } | null) =>
        option &&
        onPick({
          kb_id: knowledgeBase.id,
          doc_id: option.value,
          file_name: option.label,
        })
      }
    />
  );
}

function DocumentPicker({
  value,
  onChange,
  knowledgeBases,
}: {
  value: PickedDocument[];
  onChange: (documents: PickedDocument[]) => void;
  knowledgeBases: KnowledgeBaseOption[];
}) {
  const t = useTranslations("agentTests");
  if (knowledgeBases.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        {t("checks.fields.noKnowledgeBase")}
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-4">
      {value.length > 0 && (
        <div className="flex flex-col gap-2">
          <NoxusFormLabel label={t("checks.fields.documents")} />
          {value.map((document, index) => (
            <SelectedFileRow
              key={`${document.doc_id}-${document.file_name}`}
              name={document.file_name}
              onRemove={() => onChange(value.filter((_, i) => i !== index))}
            />
          ))}
        </div>
      )}
      {knowledgeBases.map((knowledgeBase) => (
        <KnowledgeBaseDocumentSearch
          key={knowledgeBase.id}
          knowledgeBase={knowledgeBase}
          picked={value}
          onPick={(document) => onChange([...value, document])}
        />
      ))}
    </div>
  );
}

function pickedDocuments(expected: Expected): PickedDocument[] {
  const documents = expected.documents;
  if (!Array.isArray(documents)) {
    return [];
  }
  return documents.filter(isRecord).map((d) => ({
    kb_id: typeof d.kb_id === "string" ? d.kb_id : "",
    doc_id: typeof d.doc_id === "string" ? d.doc_id : "",
    file_name: typeof d.file_name === "string" ? d.file_name : "",
  }));
}

function shortDocumentId(docId: string): string {
  return docId.replace(/-/g, "").slice(0, 4);
}

export function citedDocumentsOf(expected: Expected): {
  documents: PickedDocument[];
  unpairedIds: string[];
} {
  const fileNames = strings(expected, "known_filenames");
  const ids = strings(expected, "known_doc_ids");
  const paired = ids.length === fileNames.length;
  return {
    documents: fileNames.map((file_name, index) => ({
      kb_id: "",
      doc_id: paired ? ids[index] : "",
      file_name,
    })),
    unpairedIds: paired ? [] : ids,
  };
}

export function citedExpected(
  documents: PickedDocument[],
  unpairedIds: string[]
): Expected {
  return {
    known_filenames: documents.map((d) => d.file_name),
    known_doc_ids: [
      ...unpairedIds,
      ...documents
        .filter((d) => d.doc_id)
        .map((d) => shortDocumentId(d.doc_id)),
    ],
  };
}

function CitedDocuments({
  expected,
  onChange,
  knowledgeBases,
}: {
  expected: Expected;
  onChange: (expected: Expected) => void;
  knowledgeBases: KnowledgeBaseOption[];
}) {
  const [initial] = useState(() => citedDocumentsOf(expected));
  const [picked, setPicked] = useState<PickedDocument[]>(initial.documents);
  return (
    <DocumentPicker
      knowledgeBases={knowledgeBases}
      value={picked}
      onChange={(documents) => {
        setPicked(documents);
        onChange(citedExpected(documents, initial.unpairedIds));
      }}
    />
  );
}

function CompositeChildren({
  expected,
  onChange,
  context,
}: {
  expected: Expected;
  onChange: (expected: Expected) => void;
  context: CheckContext;
}) {
  const t = useTranslations("agentTests");
  const children = compositeChildren(expected);
  const [rowKeys, setRowKeys] = useState(() =>
    children.map(() => crypto.randomUUID())
  );
  const options = COMPOSITE_CHILD_TYPES.map((type) => ({
    value: type,
    label: checkLabel(t, type),
  }));
  const write = (next: CheckDraft[]) =>
    onChange({
      ...expected,
      children: next.map((child) => ({
        type: child.type,
        expected: child.expected,
      })),
    });
  return (
    <div className="flex flex-col gap-3">
      <NoxusFormLabel label={t("checks.fields.checksInside")} />
      {children.map((child, index) => (
        <div
          key={rowKeys[index]}
          data-testid="composite-child"
          className="border rounded-lg p-3 flex flex-col gap-3"
        >
          <div className="flex items-start gap-2">
            <div className="flex-1">
              <SpotSelect
                name={`child-${index}`}
                placeholder={t("checks.fields.chooseCheck")}
                options={options}
                value={child.type || undefined}
                onChange={(option: { value: string } | null) =>
                  write(
                    children.map((c, i) =>
                      i === index
                        ? {
                            type: option?.value ?? "",
                            expected: option
                              ? defaultExpected(option.value)
                              : {},
                          }
                        : c
                    )
                  )
                }
              />
            </div>
            <Button
              type="button"
              aria-label={t("checks.fields.removeCheckInside")}
              size="iconsm"
              variant="ghostDestructive"
              className="mt-1"
              onClick={() => {
                setRowKeys(rowKeys.filter((_, i) => i !== index));
                write(children.filter((_, i) => i !== index));
              }}
            >
              <Trash2 />
            </Button>
          </div>
          {child.type && (
            <CheckFields
              key={child.type}
              type={child.type}
              expected={child.expected}
              context={context}
              onChange={(childExpected) =>
                write(
                  children.map((c, i) =>
                    i === index ? { ...c, expected: childExpected } : c
                  )
                )
              }
            />
          )}
        </div>
      ))}
      <div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            setRowKeys([...rowKeys, crypto.randomUUID()]);
            write([...children, { type: "", expected: {} }]);
          }}
        >
          <Plus className="size-4" />
          {t("checks.fields.addCheckInside")}
        </Button>
      </div>
    </div>
  );
}

export function CheckFields({
  type,
  expected,
  onChange,
  context,
}: FieldsProps) {
  const t = useTranslations("agentTests");
  const set = (key: string) => (value: unknown) =>
    onChange({ ...expected, [key]: value });
  const toolSelect = (
    <ToolSelect
      value={text(expected, "tool_name")}
      tools={context.tools}
      onChange={(name) =>
        onChange(
          type === "agent_tool_arguments"
            ? { ...expected, tool_name: name, expected_args: {} }
            : type === "agent_tool_arg_keys"
              ? { ...expected, tool_name: name, present: [], absent: [] }
              : type === "agent_tool_argument_judge"
                ? { ...expected, tool_name: name, argument_name: "" }
                : { ...expected, tool_name: name }
        )
      }
    />
  );

  switch (type) {
    case "agent_conversation_judge":
      return (
        <TextField
          name="rubric"
          label={t("checks.fields.goodReply")}
          placeholder={t("checks.fields.goodReplyPlaceholder")}
          value={text(expected, "rubric")}
          onChange={set("rubric")}
          isTextarea
        />
      );
    case "agent_response_contains":
    case "agent_response_not_contains":
      return (
        <>
          <TextField
            name="text"
            label={t("checks.fields.text")}
            value={text(expected, "text")}
            onChange={set("text")}
          />
          <Toggle
            name="check-case-sensitive"
            label={t("checks.fields.matchCase")}
            value={expected.case_sensitive === true}
            onChange={set("case_sensitive")}
          />
        </>
      );
    case "agent_response_regex":
      return (
        <TextField
          name="pattern"
          label={t("checks.fields.pattern")}
          placeholder="\d{5}"
          value={text(expected, "pattern")}
          onChange={set("pattern")}
          helperText={
            text(expected, "pattern") && !compiles(text(expected, "pattern"))
              ? t("checks.fields.patternInvalid")
              : t("checks.fields.patternHelper")
          }
        />
      );
    case "agent_text_equals":
      return (
        <TextField
          name="text"
          label={t("checks.fields.exactReply")}
          value={text(expected, "text")}
          onChange={set("text")}
          isTextarea
        />
      );
    case "agent_llm_judge":
      return (
        <TextField
          name="reference_text"
          label={t("checks.fields.referenceAnswer")}
          value={text(expected, "reference_text")}
          onChange={set("reference_text")}
          isTextarea
        />
      );
    case "answer_correctness":
      return (
        <TextField
          name="gold_answer"
          label={t("checks.fields.expectedAnswer")}
          value={text(expected, "gold_answer")}
          onChange={set("gold_answer")}
          isTextarea
        />
      );
    case "answer_refusal":
      return <RefusalChoice expected={expected} onChange={onChange} />;
    case "agent_has_ref_tags":
      return (
        <NumberInput
          id="check-min-count"
          label={t("checks.fields.minCitations")}
          integer
          min={1}
          value={
            typeof expected.min_count === "number" ? expected.min_count : 1
          }
          onChange={set("min_count")}
        />
      );
    case "agent_ref_matches_kb_document":
      return (
        <CitedDocuments
          expected={expected}
          onChange={onChange}
          knowledgeBases={context.knowledgeBases}
        />
      );
    case "agent_retrieved_documents":
      return (
        <DocumentPicker
          knowledgeBases={context.knowledgeBases}
          value={pickedDocuments(expected)}
          onChange={set("documents")}
        />
      );
    case "agent_searched_knowledge_base":
      return (
        <>
          <MultiSelect
            name="check-kb-ids"
            label={t("checks.fields.knowledgeBases")}
            placeholder={t("checks.fields.chooseKnowledgeBases")}
            options={context.knowledgeBases.map((kb) => ({
              value: kb.id,
              label: kb.name,
            }))}
            value={strings(expected, "kb_ids").map((id) => ({
              value: id,
              label:
                context.knowledgeBases.find((kb) => kb.id === id)?.name ?? id,
            }))}
            onChange={(values) => set("kb_ids")(values.map((v) => v.value))}
          />
          <Toggle
            name="check-no-search-ok"
            label={t("checks.fields.noSearchOk")}
            value={expected.no_search_ok === true}
            onChange={set("no_search_ok")}
          />
        </>
      );
    case "agent_tool_called":
    case "agent_tool_not_called":
      return toolSelect;
    case "agent_tool_arguments":
      return (
        <>
          {toolSelect}
          {text(expected, "tool_name") && (
            <ArgumentValues
              key={text(expected, "tool_name")}
              expected={expected}
              onChange={onChange}
              tools={context.tools}
            />
          )}
        </>
      );
    case "agent_tool_arg_keys":
      return (
        <>
          {toolSelect}
          {text(expected, "tool_name") && (
            <ArgumentKeys
              expected={expected}
              onChange={onChange}
              tools={context.tools}
            />
          )}
        </>
      );
    case "agent_tool_argument_judge":
      return (
        <>
          {toolSelect}
          <SpotSelect
            name="check-argument"
            label={t("checks.fields.argument")}
            placeholder={t("checks.fields.chooseArgument")}
            options={parametersOf(
              context.tools,
              text(expected, "tool_name")
            ).map((p) => ({ value: p.name, label: p.name }))}
            value={text(expected, "argument_name") || undefined}
            isDisabled={!text(expected, "tool_name")}
            onChange={(option: { value: string } | null) =>
              set("argument_name")(option?.value ?? "")
            }
          />
          <TextField
            name="rubric"
            label={t("checks.fields.goodValue")}
            value={text(expected, "rubric")}
            onChange={set("rubric")}
            isTextarea
          />
        </>
      );
    case "agent_tool_return_judge":
      return (
        <>
          {toolSelect}
          <TextField
            name="rubric"
            label={t("checks.fields.correctResult")}
            value={text(expected, "rubric")}
            onChange={set("rubric")}
            isTextarea
          />
        </>
      );
    case "agent_tool_return_contains":
      return (
        <>
          {toolSelect}
          <TextField
            name="text"
            label={t("checks.fields.resultContains")}
            value={text(expected, "text")}
            onChange={set("text")}
          />
        </>
      );
    case "agent_any_of":
    case "agent_all_of":
      return (
        <CompositeChildren
          expected={expected}
          onChange={onChange}
          context={context}
        />
      );
    default:
      return null;
  }
}
